import * as XLSX from 'xlsx';
import varType from '../enums/varType';

/**
 * Create and download excel with provided objects as rows
 * @param dataToExport Array of any objects
 * @param filename Name of the downloaded file
 * @param sheetName Name of the sheet
 */
export function exportDataToExcel(dataToExport, filename, columns = null, sheetName = 'Daten') {
    const columnNames = [];
    const columnWidths = [];
    if (!columns) {
        Object.keys(dataToExport[0]).forEach(key => columnNames.push({label: key, value: key, width: 30}));
    } else {
        columnNames.push(...columns.map(c => {return {label: c.label, value: c.value, width: c.width?.replace?.('px', '')}; }));
    }

    parseFile(dataToExport, sheetName, columnNames, filename);
}
/**
 * Helper for calculate width (number of symbols) of the column based on provided width in px
 */
function translatePxToWidthInCharacters(px) {
    // Approx for Calibri 11: 1 character ~ 7px, with ~5px padding/offset
    // If we will update font, should also update this function
    
    // Guard for weird values
    if (px == null) return undefined;
    const width = (px - 5) / 7;
    return Math.max(0, Math.round(width * 100) / 100);
}

/**
 * Function that actually creates file based on data
 * @param data - rows
 * @param sheetName - Name of the sheet. If we will require more sheets in the future, must refactor
 * @param columnNames - Arra of objects. Should contain title, value and width
 * @param filename - Name of the exported file (without extension)
 * @returns {Promise<void>}
 */
function parseFile(data, sheetName, columnNames, filename) {
    const exportData = data.map(row => {
        const exportRow = {};
        columnNames.forEach(column => {
            exportRow[column.value] = row[column.value];
        });
        return exportRow;
    });

    // Create worksheet based on data
    const worksheet = XLSX.utils.json_to_sheet(exportData, {
        header: columnNames.map(c => c.value)
    });
    // Create full workbook
    const workbook = XLSX.utils.book_new();
    // Because column names should be localized, we replace them manually
    XLSX.utils.sheet_add_aoa(worksheet, [columnNames.map(c => c.label)], {origin: 'A1'});
    // We should build width based on already existing value (defined in Header class) or calculate on actual data
    const aoa = [
        columnNames.map(h => h.label),
        ...exportData.map(row => columnNames.map(column => row[column.value]))
    ];
    worksheet['!cols'] = buildCols(columnNames, aoa);

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${filename}.xlsx`);
}

/**
 * Calculate width of each coolumn based on data
 * @param rows - Array of Arrays. Contains data that should be exported 
 * @param colIndex - index of the current column
 * @param padding
 * @param minWidth
 * @param maxWidth
 * @returns {number}
 */
function computeWidthFromData(rows, colIndex, {padding = 2, minWidth = 0, maxWidth = 80} = {}) {
    let maxLen = 0;

    for (let r = 0; r < rows.length; r++) {
        const v = rows[r]?.[colIndex];
        if (v == null) continue;

        // Convert common types to display text
        let s = '';
        if (typeof v === varType.STRING) s = v;
        else if (typeof v === varType.NUMBER) s = String(v);
        else if (v instanceof Date) s = v.toISOString().slice(0, 10);
        // Maybe should localize to German.But in most cases it will be just 'Ja' or 'Nein', which will not extend width
        else if (typeof v === varType.BOOLEAN) s = v ? 'TRUE' : 'FALSE';
        else s = String(v);

        // Handle newlines: width should consider the longest line
        const longestLine = s.split(/\r?\n/).reduce((m, line) => Math.max(m, line.length), 0);
        maxLen = Math.max(maxLen, longestLine);
    }

    const width = Math.min(maxWidth, Math.max(minWidth, maxLen + padding));
    return width;
}

/**
 * Build minimum width of the columns
 * @param headers
 * @param rows - Array of Arrays. Actual rows. Represented s array of arrays
 * @returns {*}
 */
function buildCols(headers, rows) {
    return headers.map((h, i) => {
        const fromMinPx = translatePxToWidthInCharacters(h.width);          // undefined if minWidth undefined
        const autoWch = computeWidthFromData(rows, i);   // automatic based on content

        // If minWidth exists, enforce it; otherwise just use auto
        const wch = fromMinPx != null ? Math.max(fromMinPx, autoWch) : autoWch;
        return {wch};
    });
}